import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Category} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  TrackProduct,
  useTrackProductTileGallerySwipe,
} from '@aboutyou/tracking';
import {
  BadgeContainer,
  BaseProductTile,
  BaseProductTileFeatures,
  BaseProductTileProps,
  Button,
  COLORS,
  GenericIconName,
  Hint,
  SplitBadge,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {useSponsoredProductPopup} from '../../backend-services/SponsoredProductPopup/utils/useSponsoredProductPopup';
import {BadgeList} from '../BadgeList/BadgeList';
import {useBadgesList, useBadgesListKeys} from '../BadgeList/useBadgesList';
import {ProductTileTrackedProps} from './ProductTileTracked';
import {WishlistButton} from './WishlistButton';
import {useTargetRouteState} from './hooks/useTargetRouteState';
import {useTileTrackingData} from './hooks/useTileTrackingData';
import {AddToBasketPayload} from './types';
import {mapColorBubbles} from './utils';

/** How colour variant bubbles are shown on the tile. */
export type ProductTileColorBubblesMode =
  | 'default'
  | 'hidden'
  | 'hiddenOnMobile';

export interface ProductTileProps
  extends Omit<BaseProductTileFeatures, 'productTileGalleryMode'> {
  className?: string;
  tile: ProductTileV2;
  trackingData?: ProductTileTrackedProps['trackingData'];
  /**
   * If this product is part of a promotion (ad),
   * the add_to_wishlist and remove_from_wishlist events should use creative_name: 'ad' and promotion_id: <ad_id>.
   */
  promotionId?: string;
  streamPosition: number;
  isInViewOnRemount?: boolean;
  disableTracking?: boolean;
  onImpressionTracked?: VoidFunction;
  tileRef?: React.RefObject<HTMLDivElement | null>;
  selectedCoupon?: string;
  enableImageGalleryAnimation?: boolean;
  ProductTileComponent?: React.FC<BaseProductTileProps>;
  productTileGallery?: boolean;
  onClickAddCta?: (payload: AddToBasketPayload) => void;
  addCtaLabel?: string;
  gridColumns?: number;
  category?: Category;
  enhanceSpeed?: boolean;
  /**
   * Custom content rendered in the tile header area.
   * When provided, replaces the default WishlistButton.
   */
  headerAction?: React.ReactNode;
  /** Called when the product tile link is clicked (for feature tracking at lane level). */
  onProductTileClick?: VoidFunction;
}

export const ProductTile: React.FC<ProductTileProps> = ({
  addCtaLabel,
  className,
  disableTracking,
  enableImageGalleryAnimation,
  enhanceSpeed,
  gridColumns,
  importance,
  isInViewOnRemount,
  onClickAddCta,
  onColorClick,
  onColorHover,
  onColorLeave,
  onImpressionTracked,
  onMouseEnter,
  ProductTileComponent = BaseProductTile,
  productTileGallery,
  promotionId,
  selectedCoupon,
  streamPosition,
  tile,
  tileRef,
  trackingData,
  headerAction,
  onProductTileClick,
}) => {
  const {t} = useTranslation();
  const {isActive: isADPNewTabActive} = useIsFeatureActive('ab_adp_new_tab');
  const {sendTrackingData} = useTileTrackingData();
  const {device} = useConfig();

  const {prominent} = tile;
  const {isActive: isPriceOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const trackSwipe = useTrackProductTileGallerySwipe();

  const trackGallerySwipe = () => {
    const {isSponsored, sponsoredType} = tile.productTracker;

    trackSwipe({
      itemId: tile.productId,
      locationId: trackingData?.recoType || '',
      sponsored: isSponsored,
      sortingChannel: trackingData?.sortingChannel || '',
      sponsoredType,
    });
  };

  const to = useTargetRouteState({
    tile,
    // Auto-apply the tile's own coupon on the ADP by default, so every surface
    // gets coupon auto-application without wiring it per call site. An explicit
    // `selectedCoupon` (e.g. the page-level coupon on the Coupon Landing Page)
    // still takes precedence.
    selectedCoupon: selectedCoupon ?? tile.couponPromotionId,
    customSection: trackingData?.recoType,
    gridColumns,
  });

  const colors = useMemo(() => {
    return mapColorBubbles(tile.colors);
  }, [tile.colors]);

  const {onSponsoredClick} = useSponsoredProductPopup();

  const sponsoredProps = useMemo(() => {
    return tile.sponsored
      ? {
          label: tile.sponsored.label,
          onClick: (e: React.MouseEvent) =>
            onSponsoredClick({e, productId: tile.productId}),
        }
      : undefined;
  }, [tile.sponsored, tile.productId, onSponsoredClick]);

  const {isActive: isSponsoredProductTileUnsampledLoggingEnabled} =
    useIsFeatureActive('enable_sponsored_product_tile_unsampled_logging');

  const getBadgesConfig = {
    badges: tile.badges,
    sale: null,
    campaignSale: null,
    useDefaultLabels: true,
    showPriceOverhaulV2SaleLabel: false,
  };
  const badgesList = useBadgesList(getBadgesConfig);
  const badgesListKeys = useBadgesListKeys(getBadgesConfig);

  return (
    <StyledTrackProductWithBadges
      className={className}
      Comp="li"
      productTile={{
        product: tile.productTracker,
        brand: tile.brandTracker,
        price: tile.price,
      }}
      streamPosition={streamPosition}
      trackingKey={tile.productId}
      trackingData={trackingData}
      threshold={0.2}
      triggerOnce
      data-testid={`productTileTracker-${tile.productId}`}
      onImpressionTracked={onImpressionTracked}
      disabled={disableTracking}
      badgesListKeys={badgesListKeys}
    >
      <ProductTileComponent
        price={isPriceOverhaulV2Active ? tile.priceV2 : tile.price}
        productName={tile.productName}
        brandName={tile.brandName}
        productId={tile.productId}
        gallery={tile.gallery}
        importance={isInViewOnRemount ? 'high' : importance}
        tileRef={tileRef}
        productTileGalleryMode={productTileGallery ? 'always' : undefined}
        availableSizes={tile.availableSizes}
        colors={colors}
        trackGallerySwipe={trackGallerySwipe}
        enableImageGalleryAnimation={enableImageGalleryAnimation}
        enhanceSpeed={enhanceSpeed}
        to={to}
        onColorHover={onColorHover}
        onColorLeave={onColorLeave}
        onColorClick={onColorClick}
        onMouseEnter={onMouseEnter}
        gridColumns={gridColumns}
        openInNewTab={isADPNewTabActive && device === 'desktop'}
        onClick={() => {
          onProductTileClick?.();
          if (isADPNewTabActive && device === 'desktop') {
            sendTrackingData(trackingData, tile.productId);
          }
        }}
        cta={
          onClickAddCta && (
            <StyledAddButton
              data-testid="ProductTileAddToBasketButton"
              aria-label={addCtaLabel || t('Add to basket')}
              onClick={(e: React.MouseEvent) => {
                // this whole element is wrapped into anchor. This will prevent anchor onClick from triggering when button is pressed.
                e.stopPropagation();
                // prevent navigating to the ADP on the parent anchor element (since the event bubbles up)
                e.preventDefault();
                const payload: AddToBasketPayload = {
                  product: {
                    id: tile.productId,
                    sponsoredType: tile.productTracker.sponsoredType,
                    // no way of knowing, needs to be handled on the backend
                    captchaRequired: false,
                  },
                  price: isPriceOverhaulV2Active ? tile.priceV2 : tile.price,
                  selectedSizeId: tile.selectedSizeId,
                  couponPromotionId: tile.couponPromotionId,
                  recoType: trackingData?.recoType,
                  streamPosition,
                  badgesListKeys,
                };
                onClickAddCta(payload);
              }}
              tabIndex={-1}
            >
              {addCtaLabel || t('Add to basket')}
            </StyledAddButton>
          )
        }
        sponsored={sponsoredProps}
        isSponsoredProductTileUnsampledLoggingEnabled={
          isSponsoredProductTileUnsampledLoggingEnabled
        }
      >
        <div>
          <HeaderElements>
            {headerAction ?? (
              <WishlistButton
                brand={tile.brandTracker}
                product={tile.productTracker}
                price={isPriceOverhaulV2Active ? tile.priceV2 : tile.price}
                streamPosition={streamPosition}
                trackingData={
                  promotionId
                    ? {
                        ...trackingData,
                        creativeName: 'ad',
                        promotionId,
                      }
                    : trackingData
                }
                isFocusable={false}
                badgesListKeys={badgesListKeys}
              />
            )}
          </HeaderElements>
          {tile.badges.length > 0 && (
            <StyledBadgeList
              badgesList={badgesList}
              increaseRightSideBorderRadius
            />
          )}
          {prominent?.type?.$case === 'couponBanner' ? (
            <ProminentCouponBanner
              testId="prominentCouponBanner"
              start={{
                label: t('Coupon'),
                backgroundColor: COLORS.PrimaryCoupon,
                icon: GenericIconName.Coupon,
              }}
              end={{
                label: prominent.type.couponBanner.trailingLabel,
                backgroundColor: COLORS['Coupon.Secondary'],
              }}
              foregroundColor={COLORS.White}
            />
          ) : null}
          {prominent?.type?.$case === 'lowStockHint' && (
            <LowStockHint
              testId="lowStockHint"
              label={prominent.type.lowStockHint.label}
              fontSize="px12"
              weight="mediumBold"
            />
          )}
        </div>
      </ProductTileComponent>
    </StyledTrackProductWithBadges>
  );
};

const TrackProductWithBadges: React.FC<
  Omit<React.ComponentProps<typeof TrackProduct>, 'badges'> & {
    badgesListKeys: string[];
  }
> = ({badgesListKeys, ...otherProps}) => {
  return <TrackProduct {...otherProps} badges={badgesListKeys} />;
};

const StyledTrackProductWithBadges = styled(TrackProductWithBadges)`
  list-style: none;
`;

const HeaderElements = styled.div`
  &&& {
    padding: 15px;
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
    position: relative;
    z-index: 2;
    pointer-events: auto;
  }
`;

const LowStockHint = styled(Hint)`
  bottom: 0.4rem;
  font-weight: 500;
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  width: calc(100% - 20px);
  border-radius: 0 4px 4px 0;

  ${tabletAndUpLinaria(`
    border-radius: 4px;
    width: calc(100% - 15px);
  `)}
`;

const ProminentCouponBanner = styled(SplitBadge)`
  position: absolute;
  bottom: 4px;
`;

const StyledBadgeList = styled(BadgeList)`
  &&& {
    &:not(:last-child) {
      bottom: 37px;
    }
    ${BadgeContainer} {
      margin-top: 4px;
    }
  }
`;

const StyledAddButton = styled(Button)`
  margin-top: 8px;
  white-space: nowrap;
`;
